import React from "react";
import { motion } from "framer-motion";
import {
  Microscope, FlaskConical, ShieldCheck, Sparkles,
  Flower2, Leaf, GraduationCap, Rocket,
} from "lucide-react";

const ITEMS = [
  { icon: Microscope, title: "Research & Development", body: "Continuous investment in molecular research and clinical evidence to expand our formulation depth." },
  { icon: FlaskConical, title: "Advanced Formulations", body: "Novel drug delivery systems designed for improved bioavailability and patient compliance." },
  { icon: ShieldCheck, title: "Quality Assurance", body: "WHO-GMP aligned processes, in-process controls, and independent quality verification." },
  { icon: Sparkles, title: "Dermatology Excellence", body: "A deep dermatology portfolio spanning creams, lotions and ointments for skin health." },
  { icon: Flower2, title: "Women's Health", body: "Thoughtful therapies for hormonal balance, PCOS management and reproductive wellness." },
  { icon: Leaf, title: "Nutraceuticals", body: "Science-backed supplements combining bioactives for immunity, vitality and daily wellness." },
  { icon: GraduationCap, title: "Scientific Marketing", body: "Evidence-first medical communication that empowers doctors and healthcare partners." },
  { icon: Rocket, title: "Future-ready Healthcare", body: "Building tomorrow's therapies with an unwavering focus on safety and effectiveness." },
];

export default function Innovation() {
  return (
    <section id="innovation" className="py-24 sm:py-32 px-6 sm:px-10 lg:px-10 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
      <div className="absolute -top-16 right-0 w-1/2 h-64 bg-[radial-gradient(circle_at_top,#dbeafe,transparent_70%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="section-eyebrow" data-testid="innovation-eyebrow">Innovation</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900" data-testid="innovation-heading">
              Innovation that <br className="hidden sm:block" /> improves lives.
            </h2>
          </div>
          <p className="max-w-md text-slate-600" data-testid="innovation-subheading">
            Eight strategic pillars guide how we discover, formulate and deliver medicines that
            meaningfully change patient outcomes.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {ITEMS.map((it, i) => (
            <motion.article
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              className="group relative bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1.5 hover:shadow-xl hover:border-blue-200 transition-[transform,box-shadow,border-color] duration-300"
              data-testid={`innovation-card-${i}`}
            >
              <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-900 flex items-center justify-center group-hover:bg-blue-900 group-hover:text-white transition-colors">
                <it.icon size={18} />
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{it.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.body}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
